import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Chart from "react-apexcharts";
import { createColumnHelper } from "@tanstack/react-table";
import { FaArrowLeft, FaCircleUser } from "react-icons/fa6";
import Card from "components/card";
import ExpandableDataTable from "components/admin/ExpandableDataTable";
import { DetailField, DetailGrid, DetailSection, IdValue } from "components/admin/DetailPanel";
import StatusBadge from "components/admin/StatusBadge";
import MarketLifecycle from "components/markets/MarketLifecycle";
import MarketActionPanel from "components/markets/MarketActionPanel";
import { RESOLUTION_STATUSES, canFileDispute, formatCountdown, isDecisionDecidable, statusLabel, statusTone } from "components/markets/marketActions";
import usePortalAudience, { createdByLabel, usePartnerNames } from "components/markets/usePortalAudience";
import MarketTimeline from "components/admin/MarketTimeline";
import HomepageToggle from "components/markets/HomepageToggle";
import { ApprovalHistoryList, useApprovalHistory } from "components/backoffice/Backoffice";
import usePaginatedResource from "hooks/usePaginatedResource";
import { apiGet } from "lib/apiClient";
import { formatKes } from "lib/format";
import { useAuth } from "contexts/AuthContext";

const positionColumnHelper = createColumnHelper();
const orderColumnHelper = createColumnHelper();

// Chart colours come from the Wasaa tokens (YES = --yes, NO = --no, axes in
// --muted-foreground, gridlines in --border), read at render so dark mode applies.
function tokenValue(name, fallback) {
  if (typeof window === "undefined" || !document.body) return fallback;
  const value = window.getComputedStyle(document.body).getPropertyValue(name).trim();
  return value || fallback;
}

function chartTokens() {
  const dark = typeof document !== "undefined" && document.body.classList.contains("dark");
  return {
    dark,
    yes: tokenValue("--yes", "#2fbd85"),
    no: tokenValue("--no", "#f6465d"),
    axis: tokenValue("--muted-foreground", dark ? "#93a5c4" : "#5b6b85"),
    grid: dark ? "rgba(255, 255, 255, 0.12)" : tokenValue("--border", "#e2e8f2"),
    font: tokenValue("--font-sans", "'Open Sauce One', sans-serif"),
  };
}


const RESOLUTION_STAGES = RESOLUTION_STATUSES;
const RESOLUTION_STAGE_LABELS = {
  CLOSED: "Trading closed",
  AWAITING_RESULT: "Awaiting result",
  RESULT_PROPOSED: "Result proposed",
  RESULT_CONFIRMED: "Result confirmed",
  SETTLING: "Settling",
  SETTLED: "Settled",
};

const ORDER_STATUS_TONE = {
  PENDING: "gray",
  OPEN: "blue",
  PARTIALLY_FILLED: "amber",
  FILLED: "green",
  CANCELLED: "gray",
  REJECTED: "red",
  EXPIRED: "gray",
};

function num(value, decimals = 2) {
  const n = Number(value);
  return Number.isFinite(n) ? n.toFixed(decimals) : value;
}

function TraderCell({ trader }) {
  return (
    <div className="flex items-center gap-2">
      <FaCircleUser className="h-5 w-5 text-gray-400" />
      <div>
        <p className="text-sm font-semibold text-navy-700 dark:text-white">{trader.email || trader.phone || trader.userId}</p>
        {trader.email && trader.phone && <p className="text-xs text-gray-500">{trader.phone}</p>}
      </div>
    </div>
  );
}

export default function MarketDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const audience = usePortalAudience();
  const partnerNames = usePartnerNames(audience.isTenant);
  const [reloadKey, setReloadKey] = useState(0);
  const [performance, setPerformance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [adminMarket, setAdminMarket] = useState(null);
  const [adminDecision, setAdminDecision] = useState(undefined);
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    let cancelled = false;
    setError(null);
    apiGet(`/portal/markets/${id}/performance`)
      .then((res) => {
        if (!cancelled) setPerformance(res);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || "Failed to load market performance.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, reloadKey]);

  const tenantManager = audience.isTenant && audience.can("market.update");
  const tenantResolver = audience.isTenant && audience.can("market.settle");

  useEffect(() => {
    if (!tenantManager || !audience.tenantId) return undefined;
    let cancelled = false;
    apiGet(`/admin/tenants/${audience.tenantId}/markets/${id}`)
      .then((res) => {
        if (!cancelled) setAdminMarket(res.market);
      })
      .catch(() => {
        if (!cancelled) setAdminMarket(null);
      });
    return () => {
      cancelled = true;
    };
  }, [id, reloadKey, tenantManager, audience.tenantId]);

  const currentStatus = adminMarket?.status || performance?.status;

  useEffect(() => {
    if (!tenantResolver || !audience.tenantId || !RESOLUTION_STAGES.includes(currentStatus)) {
      setAdminDecision(undefined);
      return undefined;
    }
    let cancelled = false;
    apiGet(`/admin/tenants/${audience.tenantId}/markets/${id}/decision`)
      .then((res) => {
        if (!cancelled) setAdminDecision(res.decision);
      })
      .catch(() => {
        if (!cancelled) setAdminDecision(undefined);
      });
    return () => {
      cancelled = true;
    };
  }, [id, reloadKey, tenantResolver, audience.tenantId, currentStatus]);

  useEffect(() => {
    if (currentStatus !== "RESULT_PROPOSED") return undefined;
    const timer = setInterval(() => setNowMs(Date.now()), 15000);
    return () => clearInterval(timer);
  }, [currentStatus]);

  function reload() {
    setReloadKey((k) => k + 1);
  }

  const {
    items: positions,
    total: positionsTotal,
    loading: positionsLoading,
    error: positionsError,
    page: positionsPage,
    setPage: setPositionsPage,
    pageSize: positionsPageSize,
    refetch: refetchPositions,
    exportAll: exportPositions,
  } = usePaginatedResource(`/portal/markets/${id}/positions`, { pageSize: 20, itemsKey: "positions" });

  const {
    items: orders,
    total: ordersTotal,
    loading: ordersLoading,
    error: ordersError,
    page: ordersPage,
    setPage: setOrdersPage,
    pageSize: ordersPageSize,
    refetch: refetchOrders,
    exportAll: exportOrders,
  } = usePaginatedResource(`/portal/markets/${id}/orders`, { pageSize: 20, itemsKey: "orders" });

  const [trades, setTrades] = useState([]);
  useEffect(() => {
    apiGet(`/portal/markets/${id}/trades?page=1&pageSize=100`)
      .then((res) => setTrades(res.trades || []))
      .catch(() => setTrades([]));
  }, [id]);

  const priceSeries = useMemo(() => {
    const byOutcome = new Map();
    for (const t of [...trades].reverse()) {
      const arr = byOutcome.get(t.outcomeKey) || [];
      arr.push({ x: new Date(t.createdAt).getTime(), y: Number(t.price) });
      byOutcome.set(t.outcomeKey, arr);
    }
    return Array.from(byOutcome.entries()).map(([name, data]) => ({ name, data }));
  }, [trades]);

  const positionColumns = [
    positionColumnHelper.accessor("trader", {
      header: () => <p>TRADER</p>,
      cell: (info) => <TraderCell trader={info.getValue()} />,
    }),
    positionColumnHelper.accessor("outcomeKey", {
      header: () => <p>SIDE</p>,
      cell: (info) => <StatusBadge label={info.getValue()} tone={info.getValue() === "YES" ? "green" : "red"} />,
    }),
    positionColumnHelper.accessor("quantity", {
      header: () => <p>STAKED (SHARES)</p>,
      cell: (info) => <p className="num text-sm font-semibold text-navy-700 dark:text-white">{num(info.getValue(), 4)}</p>,
    }),
    positionColumnHelper.accessor("realizedPnl", {
      header: () => <p>REALIZED P&amp;L</p>,
      cell: (info) => (
        <p className={`num text-sm font-semibold ${Number(info.getValue()) >= 0 ? "text-green-600" : "text-red-500"}`}>{formatKes(info.getValue())}</p>
      ),
    }),
  ];

  const orderColumns = [
    orderColumnHelper.accessor("trader", {
      header: () => <p>TRADER</p>,
      cell: (info) => <TraderCell trader={info.getValue()} />,
    }),
    orderColumnHelper.accessor("outcomeKey", {
      header: () => <p>SIDE</p>,
      cell: (info) => <StatusBadge label={info.getValue()} tone={info.getValue() === "YES" ? "green" : "red"} />,
    }),
    orderColumnHelper.accessor("side", {
      header: () => <p>BUY/SELL</p>,
      cell: (info) => <StatusBadge label={info.getValue()} tone={info.getValue() === "BUY" ? "blue" : "amber"} />,
    }),
    orderColumnHelper.accessor("filledQuantity", {
      header: () => <p>FILLED</p>,
      cell: (info) => (
        <p className="num text-sm text-navy-700 dark:text-white">
          {num(info.getValue(), 4)} of {num(info.row.original.quantity, 4)}
        </p>
      ),
    }),
    orderColumnHelper.accessor("status", {
      header: () => <p>STATUS</p>,
      cell: (info) => <StatusBadge label={info.getValue()} tone={ORDER_STATUS_TONE[info.getValue()] || "gray"} />,
    }),
  ];

  function renderPositionPanel(p) {
    return (
      <div>
        <DetailSection title="How this trader stands on this market">
          <DetailGrid>
            <DetailField label="Side">{p.outcomeKey}</DetailField>
            <DetailField label="Shares staked">{num(p.quantity, 4)}</DetailField>
            <DetailField label="Average entry price">{num(p.avgEntryPrice)}</DetailField>
            <DetailField label="Fees paid">{formatKes(p.feesPaid)}</DetailField>
            <DetailField label="Realized profit and loss">{formatKes(p.realizedPnl)}</DetailField>
            <DetailField label="Settlement proceeds">{formatKes(p.settlementProceeds)}</DetailField>
          </DetailGrid>
          <p className="mt-3 overflow-x-auto whitespace-nowrap rounded-lg bg-gray-50 px-3 py-2 font-mono text-[11px] text-gray-600 dark:bg-white/5 dark:text-gray-300">
            cost basis = avg entry × shares = {num(p.avgEntryPrice)} × {num(p.quantity, 4)}
          </p>
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            Realized profit and loss counts only shares already sold or settled. Fees paid covers the 1.5% early exit fee on
            any shares this trader sold back to the pool before the market resolved.
          </p>
        </DetailSection>
      </div>
    );
  }

  function renderOrderPanel(o) {
    return (
      <div>
        <DetailSection title="Order">
          <DetailGrid>
            <DetailField label="Order id">
              <IdValue value={o.id} />
            </DetailField>
            <DetailField label="Side">{o.outcomeKey}</DetailField>
            <DetailField label="Direction">{o.side}</DetailField>
            <DetailField label="Order type">{o.limitPrice ? "Limit" : "Market"}</DetailField>
            <DetailField label="Price">{o.limitPrice ? num(o.limitPrice) : "Market price at fill"}</DetailField>
            <DetailField label="Quantity">{num(o.quantity, 4)}</DetailField>
            <DetailField label="Filled">{num(o.filledQuantity, 4)}</DetailField>
            <DetailField label="Status">{o.status}</DetailField>
            <DetailField label="Placed" span>
              {o.createdAt ? new Date(o.createdAt).toLocaleString() : "Not recorded"}
            </DetailField>
          </DetailGrid>
        </DetailSection>
      </div>
    );
  }

  if (loading) return <p className="text-sm text-gray-500">Loading market performance…</p>;
  if (error || !performance) {
    return (
      <div>
        <button onClick={() => navigate(-1)} className="mb-4 flex items-center gap-2 text-sm font-semibold text-navy-700 dark:text-white">
          <FaArrowLeft /> Back
        </button>
        <p className="text-sm font-medium text-red-500">{error || "Market not found."}</p>
      </div>
    );
  }

  const market = adminMarket ? { ...performance, ...adminMarket } : { ...performance, id: performance.marketId };
  const proposedOutcome = adminDecision ? (market.outcomes || []).find((o) => o.id === adminDecision.proposedOutcomeId) : null;
  const shownDecision = adminDecision
    ? { ...adminDecision, outcomeKey: proposedOutcome?.keyName, outcomeLabel: proposedOutcome?.label || "Unknown outcome" }
    : performance.decision;
  const who = audience.isTenant ? createdByLabel(market, audience, partnerNames) : null;
  const yesStake = performance.stakedByOutcome.find((s) => s.keyName === "YES");
  const noStake = performance.stakedByOutcome.find((s) => s.keyName === "NO");
  const ct = chartTokens();

  return (
    <div>
      <button onClick={() => navigate(-1)} className="mb-4 flex items-center gap-2 text-sm font-semibold text-navy-700 hover:underline dark:text-white">
        <FaArrowLeft /> Back to markets
      </button>

      <Card extra="p-5 mb-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-4">
            {performance.imageUrl && (
              <img src={performance.imageUrl} alt="" className="h-16 w-16 flex-shrink-0 rounded-xl object-cover" />
            )}
            <div>
              <h2 className="text-xl font-semibold text-navy-700 dark:text-white">{performance.question}</h2>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <StatusBadge label={statusLabel(market.status)} tone={statusTone(market.status)} />
                {who && <StatusBadge label={`Created by: ${who.label}`} tone={who.tone} />}
                {adminMarket && <StatusBadge label={adminMarket.publishedAt ? "On the storefront" : "Not published"} tone={adminMarket.publishedAt ? "green" : "gray"} />}
                {audience.isTenant && market.partnerId && <StatusBadge label={`Partner market · ${partnerNames[market.partnerId] || "partner"}`} tone="blue" />}
                {audience.isTenant && market.homepageHidden === true && <StatusBadge label="Off homepage" tone="amber" />}
              </div>
            </div>
          </div>
        </div>

        {audience.isTenant && (
          <div className="mt-4">
            <HomepageToggle marketId={market.id} hidden={market.homepageHidden} canEdit={audience.canManage} onChanged={reload} />
          </div>
        )}

        <div className="mt-4">
          <MarketLifecycle market={market} portal={audience.isTenant ? "admin" : "partner"} compact={audience.isTenant} />
        </div>

        {audience.canManage && (
          <div className="mt-4 rounded-xl border border-gray-200 p-4 dark:border-white/10">
            <p className="text-sm font-semibold text-navy-700 dark:text-white">Manage this market</p>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {audience.isTenant
                ? "Only the steps valid at its current status are shown. Approving, publishing and confirming a result each need a second person: not the one who created the market or proposed the result."
                : "Only the steps valid at its current status are shown. Approval, publishing and results are handled by your tenant."}
            </p>
            <div className="mt-3">
              <MarketActionPanel
                market={market}
                audience={audience}
                decision={adminDecision}
                full={Boolean(adminMarket)}
                onChanged={reload}
                emptyMessage="Nothing for you to do at this status."
              />
            </div>
          </div>
        )}

        <div className="mt-5 grid grid-cols-2 gap-4 md:grid-cols-4">
          <div className="rounded-xl border border-gray-100 p-4 dark:border-white/10">
            <p className="text-xs font-medium text-muted-foreground">Total volume</p>
            <p className="num mt-1 text-lg font-semibold text-navy-700 dark:text-white">{formatKes(performance.totalVolume)}</p>
          </div>
          <div className="rounded-xl border border-gray-100 p-4 dark:border-white/10">
            <p className="text-xs font-medium text-muted-foreground">Trades</p>
            <p className="num mt-1 text-lg font-semibold text-navy-700 dark:text-white">{performance.tradeCount}</p>
          </div>
          <div className="rounded-xl border border-gray-100 p-4 dark:border-white/10">
            <p className="text-xs font-medium text-muted-foreground">Unique traders</p>
            <p className="num mt-1 text-lg font-semibold text-navy-700 dark:text-white">{performance.uniqueTraderCount}</p>
          </div>
          <div className="rounded-xl border border-gray-100 p-4 dark:border-white/10">
            <p className="text-xs font-medium text-muted-foreground">YES vs NO shares staked</p>
            <p className="num mt-1 text-lg font-semibold text-navy-700 dark:text-white">
              <span className="text-green-600">{num(yesStake?.totalStaked || "0", 2)}</span>
              {" / "}
              <span className="text-red-500">{num(noStake?.totalStaked || "0", 2)}</span>
            </p>
          </div>
        </div>
      </Card>

      {RESOLUTION_STAGES.includes(market.status) && (
        <Card extra="p-5 mb-5">
          <p className="text-lg font-semibold text-navy-700 dark:text-white">Resolution</p>
          <p className="mt-1 text-xs text-gray-600 dark:text-gray-300">
            {audience.isTenant
              ? "Trading has closed. Your team proposes the real-world outcome, a second team member confirms it, and settlement pays correct traders. This updates as it moves through review."
              : "Trading has closed. Your tenant confirms the real-world outcome and settles positions on the correct outcome. This updates automatically as it moves through review."}
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {RESOLUTION_STAGES.map((stage, i) => {
              const currentIndex = RESOLUTION_STAGES.indexOf(market.status);
              const isDone = i < currentIndex;
              const isCurrent = i === currentIndex;
              return (
                <div key={stage} className="flex items-center gap-2">
                  <span
                    className={`rounded-full px-3 py-1.5 text-xs font-semibold ${
                      isCurrent
                        ? "bg-primary text-primary-foreground"
                        : isDone
                          ? "bg-green-50 text-green-600 dark:bg-green-400/10"
                          : "bg-gray-100 text-gray-400 dark:bg-white/5"
                    }`}
                  >
                    {RESOLUTION_STAGE_LABELS[stage]}
                  </span>
                  {i < RESOLUTION_STAGES.length - 1 && <span className="text-gray-300 dark:text-white/20">→</span>}
                </div>
              );
            })}
          </div>

          {shownDecision && (
            <div className="mt-4 rounded-xl border border-gray-100 p-4 dark:border-white/10">
              <div className="flex flex-wrap items-center gap-2">
                <StatusBadge label={shownDecision.outcomeKey || shownDecision.outcomeLabel} tone={shownDecision.outcomeKey === "NO" ? "red" : "green"} />
                <p className="text-sm font-semibold text-navy-700 dark:text-white">
                  {shownDecision.status === "PENDING" ? "Proposed outcome" : shownDecision.status === "REJECTED" ? "Rejected proposal" : "Confirmed outcome"}: {shownDecision.outcomeLabel}
                </p>
              </div>
              {shownDecision.evidence && <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">{shownDecision.evidence}</p>}
              {shownDecision.proposedBy && (
                <p className="mt-2 text-xs text-gray-500">
                  Proposed by {shownDecision.proposedBy === audience.userId ? "you" : "a teammate"}
                  {shownDecision.decidedBy ? `, decided by ${shownDecision.decidedBy === audience.userId ? "you" : "a teammate"}` : ""}.
                </p>
              )}
              {shownDecision.disputedAt ? (
                <div className="mt-3 rounded-lg bg-red-50 p-2.5 dark:bg-red-400/10">
                  <p className="text-xs font-semibold text-red-600 dark:text-red-300">Disputed</p>
                  {shownDecision.disputeReason && <p className="mt-1 text-xs text-red-600/90 dark:text-red-300/80">{shownDecision.disputeReason}</p>}
                </div>
              ) : (
                shownDecision.status === "PENDING" &&
                shownDecision.disputeWindowEndsAt &&
                (isDecisionDecidable(shownDecision, nowMs) ? (
                  <p className="mt-3 text-xs text-gray-500">The dispute window has closed with no dispute filed.</p>
                ) : (
                  <p className="mt-3 rounded-lg bg-amber-50 p-2.5 text-xs font-semibold text-amber-700 dark:bg-amber-400/10 dark:text-amber-300">
                    Dispute window closes in {formatCountdown(new Date(shownDecision.disputeWindowEndsAt).getTime() - nowMs)}
                    {canFileDispute(shownDecision, nowMs) && audience.isTenant ? ". A teammate can dispute it from Manage this market." : "."}
                  </p>
                ))
              )}
            </div>
          )}
        </Card>
      )}

      <div className="mb-5 grid grid-cols-1 gap-5 xl:grid-cols-3">
        <Card extra="p-5 xl:col-span-2">
          <p className="text-lg font-semibold text-navy-700 dark:text-white">Price history</p>
          <p className="mt-1 text-xs text-gray-600 dark:text-gray-300">Every executed trade price for each side, over time.</p>
          <div className="mt-3 h-[280px]">
            {priceSeries.length === 0 ? (
              <p className="mt-10 text-center text-sm text-gray-500">No trades yet.</p>
            ) : (
              <Chart
                type="scatter"
                width="100%"
                height="100%"
                series={priceSeries}
                options={{
                  chart: { toolbar: { show: false }, zoom: { enabled: false }, fontFamily: ct.font, foreColor: ct.axis, background: "transparent" },
                  colors: [ct.yes, ct.no],
                  markers: { size: 4, strokeWidth: 0 },
                  xaxis: { type: "datetime", labels: { style: { colors: ct.axis } }, axisBorder: { color: ct.grid }, axisTicks: { color: ct.grid } },
                  yaxis: { min: 0, max: 1, labels: { style: { colors: ct.axis } } },
                  legend: { show: true, labels: { colors: ct.axis } },
                  grid: { strokeDashArray: 4, borderColor: ct.grid },
                  tooltip: { theme: ct.dark ? "dark" : "light", x: { format: "dd MMM HH:mm" } },
                }}
              />
            )}
          </div>
        </Card>

        <Card extra="p-5">
          <p className="text-lg font-semibold text-navy-700 dark:text-white">Stake by side</p>
          <p className="mt-1 text-xs text-gray-600 dark:text-gray-300">Shares currently held, YES vs NO.</p>
          <div className="mt-3 h-[280px]">
            <Chart
              type="bar"
              width="100%"
              height="100%"
              series={[{ name: "Shares staked", data: [Number(yesStake?.totalStaked || 0), Number(noStake?.totalStaked || 0)] }]}
              options={{
                chart: { toolbar: { show: false }, fontFamily: ct.font, foreColor: ct.axis, background: "transparent" },
                colors: [ct.yes, ct.no],
                plotOptions: { bar: { distributed: true, borderRadius: 6, columnWidth: "50%" } },
                xaxis: { categories: ["YES", "NO"], labels: { style: { colors: ct.axis } }, axisBorder: { color: ct.grid }, axisTicks: { color: ct.grid } },
                yaxis: { labels: { style: { colors: ct.axis } } },
                dataLabels: { enabled: false },
                grid: { strokeDashArray: 4, borderColor: ct.grid },
                legend: { show: false },
                tooltip: { theme: ct.dark ? "dark" : "light" },
              }}
            />
          </div>
        </Card>
      </div>

      <div className="mb-5">
        <ExpandableDataTable
          title="Positions: net stake per trader"
          subtitle="Open a trader for their entry price, fees and settlement proceeds."
          renderDetail={(p) => renderPositionPanel(p)}
          columns={positionColumns}
          data={positions}
          loading={positionsLoading}
          error={positionsError}
          onRetry={refetchPositions}
          emptyMessage="No one has taken a position in this market yet."
          page={positionsPage}
          pageSize={positionsPageSize}
          total={positionsTotal}
          onPageChange={setPositionsPage}
          exportFilename={`market-${id}-positions`}
          onExportAll={exportPositions}
        />
      </div>

      <div className="mb-5">
        <ExpandableDataTable
          title="Orders: every buy and sell"
          subtitle="Open an order for its type, price and full fill detail."
          renderDetail={(o) => renderOrderPanel(o)}
          columns={orderColumns}
          data={orders}
          loading={ordersLoading}
          error={ordersError}
          onRetry={refetchOrders}
          emptyMessage="No orders have been placed on this market yet."
          page={ordersPage}
          pageSize={ordersPageSize}
          total={ordersTotal}
          onPageChange={setOrdersPage}
          exportFilename={`market-${id}-orders`}
          onExportAll={exportOrders}
        />
      </div>

      <ApprovalDecisions marketId={id} reloadKey={reloadKey} />

      <Card extra="p-5">
        <p className="text-lg font-semibold text-navy-700 dark:text-white">Activity timeline</p>
        <p className="mt-1 text-xs text-gray-600 dark:text-gray-300">Every step this market has gone through, as it happened.</p>
        <div className="mt-4">
          <MarketTimeline key={reloadKey} fetchUrl={`/portal/markets/${id}/events`} currentUserId={user?.id} apiGet={apiGet} />
        </div>
      </Card>
    </div>
  );
}

function ApprovalDecisions({ marketId, reloadKey }) {
  const state = useApprovalHistory({ marketId, pageSize: 100, reloadKey });
  return (
    <Card extra="p-5">
      <p className="text-lg font-semibold text-navy-700 dark:text-white">Approval history</p>
      <p className="mb-3 mt-1 text-xs text-gray-600 dark:text-gray-300">Each review decision on this market, who made it, when, and the notes they left.</p>
      <ApprovalHistoryList state={state} showMarket={false} empty="No review decisions on this market yet." />
    </Card>
  );
}
